import { TerciarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import Toggle from 'components/ReusableComponents/Toggle'
import ConfigureWidgets, { widgetNames } from 'features/ConfigureWidgets'
import WidgetCampaignCard from 'features/Widgets/CampaignCard'
import EditWidgetPromoDialog, {
  EditWidgetPromoWithoutDialog
} from 'features/Widgets/EditWidgetPromoDialog'
import ExcludedPagesDialog from 'features/Widgets/ExcludedPagesDialog'
import WidgetsCard from 'features/Widgets/WidgetsCard'
import WidgetSnippet from 'features/Widgets/WidgetSnippet'
import { useInternalAdmin } from 'hooks'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import widgetsApi from 'services/api/widgets'

import styles from './Widgets.module.scss'

const { thisWeek, last7days, last14days, last30days, minus1Month } = smartDateTypes

const Widgets = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [data, setData] = useState([])

  const [fromDate, setFromDate] = useState(moment().subtract(1, 'month'))
  const [toDate, setToDate] = useState(moment())

  const [isIntegrated, setIsIntegrated] = useState(null)
  const [isOnline, setIsOnline] = useState(null)

  const [selectedDeviceData, setSelectedDeviceData] = useState([])

  const [devices, setDevices] = useState('both')

  const [widgetKindBeingEdited, setWidgetKindBeingEdited] = useState()

  const [isCheckingStatus, setIsCheckingStatus] = useState(false)

  const [promotion, setPromotion] = useState({})

  const [siteMap, setSiteMap] = useState([])

  const [isSavingPromotion, setIsSavingPromotion] = useState(false)

  const [isPromotionEditDialogVisible, setIsPromotionEditDialogVisible] = useState(false)

  const [isSnippetDialogVisible, setIsSnippetDialogVisible] = useState(false)

  const [isExclusionDialogVisible, setIsExclusionDialogVisible] = useState(false)

  const [isPopupDialogVisible, setIsPopupDialogVisible] = useState(false)

  const [hotelName, setHotelName] = useState('')

  const [textMarketingUrlKey, setTextMarketingUrlKey] = useState('')

  const { addToast } = useToasts()

  const isInternalAdmin = useInternalAdmin()

  function handleSavePromotion({
    contentId,
    promotionCode,
    minNightsToStay,
    maxDiscountPercentage,
    promotionTagline
  }) {
    setIsSavingPromotion(true)

    widgetsApi
      .updatePromotion({
        contentId,
        promotionCode,
        minNightsToStay,
        maxDiscountPercentage,
        promotionTagline
      })
      .then(res => {
        setPromotion(res)
        addToast('Promotion is updated', { appearance: 'success' })
      })
      .finally(() => {
        setIsSavingPromotion(false)
        setIsPromotionEditDialogVisible(false)
      })
  }

  function checkWidgetStatus() {
    setIsCheckingStatus(true)
    widgetsApi
      .checkStatus()
      .then(res => {
        setIsOnline(res.isOnline)
        setIsIntegrated(res.isIntegrated)
      })
      .catch(() => {
        setIsOnline(false)
      })
      .finally(() => {
        setIsCheckingStatus(false)
      })
  }

  function toggleWidget(isActive, kind, widgetId) {
    widgetsApi.list
      .post({
        data: [
          {
            isActive,
            kind,
            widgetId
          }
        ]
      })
      .then(() => {
        widgetsApi
          .analytics({ fromDate: fromDate.toDate(), toDate: toDate.toDate() })
          .then(response => {
            setData(response.facts)
          })
      })
  }

  useEffect(() => {
    widgetsApi
      .analytics({ fromDate: fromDate.toDate(), toDate: toDate.toDate() })
      .then(response => {
        setData(response.facts)
        setIsIntegrated(response.isIntegrated)
        setPromotion(response.promotion)
        setIsOnline(response.isOnline)
        setSiteMap(response.sitemaps)
        setHotelName(response?.hotelName)
        setTextMarketingUrlKey(response?.textMarketingUrlKey)
      })
      .catch(error => {
        console.warn(error)
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [fromDate, toDate])

  useEffect(() => {
    if (!data) {
      return
    }

    if (devices === 'both') {
      setSelectedDeviceData(
        data
          .filter(item => item.deviceKind === 'Desktop')
          .map(item => ({
            ...item,
            totalConversions:
              data.find(mobileItem => mobileItem.deviceKind === 'Mobile' && item.kind)
                .totalConversions + item.totalConversions,
            totalEngagement:
              data.find(mobileItem => mobileItem.deviceKind === 'Mobile' && item.kind)
                .totalEngagement + item.totalEngagement,
            totalImpressions:
              data.find(mobileItem => mobileItem.deviceKind === 'Mobile' && item.kind)
                .totalImpressions + item.totalImpressions
          }))
      )
    }

    if (devices === 'desktop') {
      setSelectedDeviceData(data.filter(item => item.deviceKind === 'Desktop'))
    }

    if (devices === 'mobile') {
      setSelectedDeviceData(data.filter(item => item.deviceKind === 'Mobile'))
    }
  }, [devices, data])

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (isErred) {
    return <div>Something went wrong</div>
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Grow your audience" width={900} />
      {isPromotionEditDialogVisible && (
        <EditWidgetPromoDialog
          {...promotion}
          onSave={handleSavePromotion}
          isSaving={isSavingPromotion}
          onClose={() => setIsPromotionEditDialogVisible(false)}
        />
      )}
      {!isIntegrated && (
        <div style={{ marginBottom: 60 }}>
          <WidgetSnippet
            hasWrapper={false}
            isOnline={isOnline}
            onCheckClick={checkWidgetStatus}
            isChecking={isCheckingStatus}
          />
        </div>
      )}
      {!promotion?.promotionCode && isIntegrated && (
        <div className={styles.promotionHolder}>
          <EditWidgetPromoWithoutDialog
            {...promotion}
            onSubmit={handleSavePromotion}
            isSaving={isSavingPromotion}
          />
        </div>
      )}
      {widgetKindBeingEdited && (
        <ConfigureWidgets
          widgetKind={widgetKindBeingEdited}
          hotelName={hotelName}
          textMarketingUrlKey={textMarketingUrlKey}
          onClose={() => setWidgetKindBeingEdited(null)}
        />
      )}
      <div
        style={{
          opacity: isIntegrated && promotion?.promotionCode ? 1 : 0.4,
          pointerEvents: isIntegrated && promotion?.promotionCode ? 'auto' : 'none'
        }}
      >
        <div className={styles.titleHolder}>
          <p className={styles.title}>Widgets</p>
          {isIntegrated && promotion?.promotionCode && (
            <div className={styles.filters}>
              <Dropdown
                value={devices}
                className={styles.devicesDropdown}
                onChange={e => setDevices(e.target.value)}
                options={{
                  both: 'Desktop & mobile',
                  desktop: 'Desktop',
                  mobile: 'Mobile'
                }}
              />
              <SmartDatepicker
                startDate={fromDate}
                endDate={toDate}
                onChangeStartDate={setFromDate}
                onChangeEndDate={setToDate}
                smartDates={[thisWeek, last7days, last14days, last30days, minus1Month]}
              />
            </div>
          )}
        </div>
        <Table widths={[50, 400, 100, 100, 120, 140]}>
          <TitleRow>
            <TitleCell></TitleCell>
            <TitleCell>Widget type</TitleCell>
            <TitleCell>Impressions</TitleCell>
            <TitleCell>Engagement</TitleCell>
            <TitleCell>Conversions</TitleCell>
            <TitleCell></TitleCell>
          </TitleRow>
          <div className={styles.rows}>
            {selectedDeviceData.map((row, index) => (
              <Row key={index} className={styles.row}>
                <Cell className={styles.statusCell}>
                  <Toggle
                    checked={row.isActive}
                    disabled={!(isIntegrated && promotion?.promotionCode)}
                    handleChange={e =>
                      toggleWidget(e.target.checked, row.kind, row.widgetId)
                    }
                  />
                </Cell>
                <Cell className={styles.widgetTypeCell}>{widgetNames[row.kind]}</Cell>
                <Cell className={styles.dataCell}>{row.totalImpressions}</Cell>
                <Cell className={styles.dataCell}>{row.totalEngagement}</Cell>
                <Cell className={styles.dataCell}>{row.totalConversions}</Cell>
                <Cell className={styles.actionsCell}>
                  {isIntegrated &&
                    promotion?.promotionCode &&
                    (row.kind === 'BottomBar' || row.kind === 'TopBar') && (
                      <TerciarySmallButton
                        onClick={() => setWidgetKindBeingEdited(row.kind)}
                        style={{ minWidth: 70 }}
                      >
                        Customize
                      </TerciarySmallButton>
                    )}
                </Cell>
              </Row>
            ))}
          </div>
        </Table>
        <div className={styles.cards}>
          <WidgetsCard
            title="Offer dialog"
            description="Customize the pop-up"
            type="dialog"
            onClick={() => setIsPopupDialogVisible(true)}
          />
          <WidgetsCard
            title="Promotion"
            description={promotion?.promotionTagline}
            type="promotion"
            onClick={() => setIsPromotionEditDialogVisible(true)}
          />
          <WidgetsCard
            title="Widget snippet"
            description={
              <>
                {isOnline && (
                  <div className={styles.status}>
                    <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
                      <circle cx={8} cy={8} r={5} fill="#66BB6A" />
                    </svg>
                    Online
                  </div>
                )}
                {!isOnline && (
                  <div className={styles.status}>
                    <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
                      <circle cx={8} cy={8} r={5} fill="#FF7043" />
                    </svg>
                    Not found
                  </div>
                )}
              </>
            }
            type="snippet"
            onClick={() => setIsSnippetDialogVisible(true)}
          />
          {siteMap?.length > 0 && (
            <WidgetsCard
              title="Excluded pages"
              description="Hide on specific pages"
              type="exclude"
              onClick={() => setIsExclusionDialogVisible(true)}
            />
          )}
        </div>

        {isInternalAdmin && (
          <>
            <p className={styles.widgetCampaignsTitle}>Widget campaigns</p>
            <div className={styles.cards}>
              <WidgetCampaignCard
                title="Consent request"
                description="Will be sent to ask consent from guests using the widgets from their phones."
                campaignName="OptInRequest"
              />
              <WidgetCampaignCard
                title="Widget welcome"
                description="Will be sent after guests gave express written consent."
                campaignName="OptedIn"
              />
              <WidgetCampaignCard
                title="Next day reminder"
                description="Will be sent to guests if they have missing name or email 24 hours subscription."
                campaignName="MissingDetailReminder1"
              />
              <WidgetCampaignCard
                title="Next week reminder"
                description="Will be sent to guests if they have missing name or email 7 days after subscription."
                campaignName="MissingDetailReminder2"
              />
            </div>
          </>
        )}
      </div>
      {isSnippetDialogVisible && (
        <ConfirmationDialog
          onClose={() => setIsSnippetDialogVisible(false)}
          title="Widget snippet"
          theme="blacklist"
          maxWidth={600}
          hasConfirmButton={false}
        >
          <WidgetSnippet
            hasWrapper={false}
            isOnline={isOnline}
            hasTitle={false}
            onCheckClick={checkWidgetStatus}
            isChecking={isCheckingStatus}
          />
        </ConfirmationDialog>
      )}
      {isExclusionDialogVisible && (
        <ExcludedPagesDialog
          onClose={() => setIsExclusionDialogVisible(false)}
          siteMap={siteMap}
        />
      )}
      {isPopupDialogVisible && (
        <ConfigureWidgets onClose={() => setIsPopupDialogVisible(false)} />
      )}
    </div>
  )
}

export default Widgets
